import { batch, reactive, untrack } from '@rehaje/reactive'
import {
  getPrototypeOf,
  isArray,
  isPrivateKey,
  isTerminateValue,
  keys,
  noop,
  objectPrototype,
  uid
} from '@rehaje/shared'

import { RR } from '../alias'
import { FatalAssertInstanceError } from '../errors'
import { MountStoreEvent, UnmountStoreEvent } from '../events/Store'
import { LifeCycle } from '../models/LifeCycle'
import { TypedNode } from '../models/TypedNode'
import { isSkipCreateTypedNode } from '../skip'
import { DefinitionStore } from './DefinitionStore'
import { DependencyStore } from './DependencyStore'
import { PluginStore } from './PluginStore'

import type { ViewModel } from '../models/ViewModel'
import type {
  Clipboard,
  Collaborator,
  CreateTypedNodeContext,
  CreateTypedNodeOptions,
  RecursionRender,
  StoreOptions,
  Transform,
  TypedSpecific
} from '../types'
import type { ObserveEvent } from '@rehaje/reactive'
import type { TRecord } from '@rehaje/shared'

/**
 * Rehaje 状态中心(最核心入口)
 */
export class Store<T extends TypedSpecific = TypedSpecific> extends LifeCycle {
  static __r_renderer: RecursionRender = noop

  override __r_debug_id = `rehajeStore:${uid()}`

  /**
   * 根节点
   */
  __r_node!: TypedNode

  /**
   * 根节点版本
   */
  @reactive()
  __r_node_version = 0

  /**
   * 自定义渲染函数
   */
  __r_render?: RecursionRender

  /**
   * 全局变量
   */
  __r_variables?: TRecord

  /**
   * 复制粘贴引擎
   */
  clipboard?: Clipboard

  /**
   * 协同引擎
   */
  collaborator?: Collaborator

  /**
   * 类型定义中心
   */
  definition: DefinitionStore

  /**
   * 依赖管理中心
   */
  dependency: DependencyStore

  /**
   * 状态中心初始参数
   */
  options: StoreOptions

  /**
   * 插件中心
   */
  plugin: PluginStore

  /**
   * 根元素节点
   */
  get element() {
    return this.node.getViewElement()
  }

  /**
   * 根视图模型
   */
  get model(): Transform<T, RehajeViewModels> & ViewModel {
    if (this.node instanceof TypedNode) return this.node.getViewModel() as any
    return undefined as any
  }

  /**
   * 命名空间，规则 `rehajeStore:${options.namespace}`
   */
  get namespace() {
    const namespace = this.options.namespace ?? this.node.id
    return namespace.startsWith('rehajeStore:')
      ? namespace
      : `rehajeStore:${namespace}`
  }

  /**
   * 根节点(可读可写)
   */
  get node(): Transform<T, RehajeTypedNodes> & TypedNode {
    void this.__r_node_version
    return this.__r_node as any
  }

  set node(node: any) {
    const oldNode = this.__r_node
    const newNode = this.createTypedNode(node)
    if (oldNode === newNode) return
    if (this.__r_initialized) {
      if (!(newNode instanceof TypedNode))
        throw new FatalAssertInstanceError('TypedNode')
      this.__r_node = newNode
      this.__r_node_version++
    } else this.__r_node = newNode
    oldNode?.dispose()
  }

  /**
   * 渲染函数
   */
  get render(): RecursionRender {
    return Store.__r_renderer
  }

  /**
   * 根视图作用域
   */
  get scope() {
    return this.node.getViewScope()
  }

  /**
   * 全局变量数据
   */
  get variables(): TRecord {
    return this.scope.variables
  }

  constructor(specific: T, options: StoreOptions = {}) {
    super(options.effects)
    this.__r_variables = options.variables
    this.options = options
    this.plugin = new PluginStore(this)
    this.definition = new DefinitionStore(this)
    this.dependency = new DependencyStore(this)
    this.node = specific
    this.clipboard = options.clipboard
    this.collaborator = options.collaborator
    if (this.clipboard) this.clipboard.store = this
    if (this.collaborator) this.collaborator.store = this
    this.addEffect(() => {
      const prevOnDocChanged = this.collaborator?.options.onDocChanged
      const prevOnReactiveChanged = this.collaborator?.options.onReactiveChanged
      const dispose = this.collaborator?.effect(this.node, {
        onDocChanged: (events, next, origin) => {
          this.__r_transactCollaborator(() => next(events), origin)
          prevOnDocChanged?.(events, next, origin)
        },
        onReactiveChanged: (event, index, events, next) => {
          next(event)
          this.__r_fixCollaboratorEvents(event, index, events)
          prevOnReactiveChanged?.(event, index, events, next)
        }
      })
      this.dispatchEvent(new MountStoreEvent())
      return () => {
        dispose?.()
        this.dispatchEvent(new UnmountStoreEvent())
      }
    })
    this.__r_initialized = true
  }

  /**
   * 创建 Store API(高性能优化)
   */
  static createStore<T extends TypedSpecific>(
    specific: T,
    options: StoreOptions = {}
  ): Store<T> {
    return untrack(() => {
      return RR.unbatch(() => {
        return RR.undispatch(() => {
          return new Store(specific, options)
        })
      })
    })
  }

  /**
   * 注册渲染器(主要由渲染根组件内部注册)
   * @param render
   */
  static registerRecursionRender(renderer?: RecursionRender): void {
    if (renderer !== undefined && renderer !== this.__r_renderer)
      this.__r_renderer = renderer
  }

  /**
   * 精确识别由于移动模式带来的隐式删除行为，并修正后续事件的偏移
   */
  __r_fixCollaboratorEvents(
    event: ObserveEvent,
    index: number,
    events: ObserveEvent[]
  ): void {
    if (event.type !== 'splice') return
    if (!event.origins?.includes(TypedNode.__r_origin)) return
    const initialPath = event.initialPath.join(',')
    const offset = event.items.length - event.deleteCount
    if (!offset) return
    for (let i = index + 1; i < events.length; i++) {
      const current = events[i]
      if (current.type !== 'splice' || current.start <= event.start) continue
      if (current.initialPath.join(',') !== initialPath) continue
      current.start += offset
    }
  }

  /**
   * 包装函数内所有建模过程都变成移动模式，并且会禁用掉由于移动模式带来的隐式删除行为
   */
  __r_transactCollaborator(callback: () => void, origin?: any): void {
    batch(
      () => {
        const prevIncreaseID = TypedNode.__r_increase_id
        const prevTransactingIDs = TypedNode.__r_transacting_ids
        try {
          TypedNode.__r_increase_id = false
          TypedNode.__r_transacting_ids = []
          callback()
        } finally {
          TypedNode.__r_increase_id = prevIncreaseID
          TypedNode.__r_transacting_ids = prevTransactingIDs
        }
      },
      { origin }
    )
  }

  /**
   * 基于给定数据创建 TypedNode
   * @param specific
   * @param options
   */
  createTypedNode<T extends TypedSpecific>(
    specific: T,
    options?: CreateTypedNodeOptions
  ): Transform<T, RehajeTypedNodes> {
    if (options?.defaultsMerge && !TypedNode.__r_defaults_merge) {
      return TypedNode.__r_batchDefaultsMerge(() => {
        return this.createTypedNode(specific, options)
      })
    }

    try {
      return this.plugin.applyMiddleware(
        options?.parent,
        'createTypedNode',
        {
          node: options?.parent,
          path: options?.belongs ?? [],
          payload: specific,
          store: this
        },
        (ctx: CreateTypedNodeContext) => {
          const specific = ctx.payload
          const parent = ctx.node
          if (isTerminateValue(specific) || isSkipCreateTypedNode(specific))
            return specific
          if (specific?.type) {
            const path = ctx.path
            const TypedNodeClass = this.plugin.getTypedNodeClass(specific.type)
            if (TypedNodeClass) {
              const migration = this.plugin.getMigration(
                specific.type,
                specific.version
              )
              return TypedNode.__r_createTypedNode(
                TypedNodeClass,
                migration ? migration(specific) : specific,
                parent,
                path,
                this
              )
            }
          }
          if (isArray(specific)) {
            return specific.map((specific, index) =>
              this.createTypedNode(specific, {
                belongs: ctx.path.concat(index),
                parent
              })
            )
          } else if (getPrototypeOf(specific) !== objectPrototype) {
            return specific
          }
          const obj: TRecord = {}
          for (const key of keys(specific)) {
            if (isPrivateKey(key)) continue
            obj[key] = this.createTypedNode(specific[key], {
              belongs: ctx.path.concat(key),
              parent
            })
          }
          return obj
        }
      ) as Transform<T, RehajeTypedNodes>
    } catch (error) {
      if (error instanceof Error) {
        console.error(error)
        this.raise(error)
      } else {
        throw error
      }
      return undefined as any
    }
  }

  override dispose() {
    super.dispose()
    this.node.dispose(true, true)
  }

  /**
   * effect store 及 store.node
   * store.model 的 effect 由 RecursionView 负责，dispose 同理
   */
  override effect() {
    const unmounted = this.__r_unmounted
    super.effect()
    if (unmounted) this.node.effect(true, true)
    return () => this.dispose()
  }

  getStore() {
    return this
  }
}
